/**
 * /comparisons/chatgpt — "Draftlize vs ChatGPT for product work"
 * comparison page. Targets the "chatgpt for product manager" /
 * "ai chat memory alternative" keyword cluster.
 */
import { MarketingShell } from '../MarketingShell';

export function ChatGpt() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/chatgpt"
      documentTitle="ChatGPT for Product Managers: Why It Loses the Thread (and the Fix) · Draftlize"
      documentDescription="ChatGPT is brilliant in the moment but contradicts itself over a long project — forgetting decisions and building on stale assumptions. See why, and how Draftlize keeps every product decision addressable for PMs."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs ChatGPT</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            ChatGPT is brilliant —<br/>
            <em>until it starts contradicting itself.</em>
          </h1>
          <p className="lede">
            Chat long enough and it forgets what you decided. A definition quietly drifts. You change one assumption and it keeps building on the old one — and never tells you. This isn't about a smarter model; switching to Claude or Gemini won't fix it. It's the shape of "chat" itself: it stores your thinking as a decaying, disconnected stream of messages. Draftlize stores it differently.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">The problem isn't a dumb AI. <em>It's where it keeps your decisions.</em></h2>
          </div>
          <p>ChatGPT is a fine chat tool — no argument there. But chat is a terrible place to *keep* decisions, and that's the real reason it drifts out of sync over a long project.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>ChatGPT / Claude / Gemini</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Weeks in, still remembers what you decided</td><td className="ours yes">Decisions become cards, permanent</td><td className="no">Chat history decays</td></tr>
            <tr><td>Change one thing, what it breaks gets flagged</td><td className="ours yes">Dependency graph + stale marks</td><td className="no">Builds on stale assumptions silently</td></tr>
            <tr><td>Self-contradictions surface on their own</td><td className="ours yes">Conflict detection across discussions</td><td className="no">Never cross-checks</td></tr>
            <tr><td>Every decision has a fixed address to cite</td><td className="ours yes">Cards are addressable (URL, ID)</td><td className="no">Buried in the chat stream</td></tr>
            <tr><td>Scattered ideas converge into a plan</td><td className="ours yes">The card graph accrues</td><td className="partial">Left in chat, you dig it out</td></tr>
            <tr><td>Open a new chat, the context is still there</td><td className="ours yes">The card graph IS the context</td><td className="partial">Memory summary, best-effort</td></tr>
            <tr><td>Same context from Claude Code / Cursor</td><td className="ours yes">MCP + CLI</td><td className="no">Each tool remembers separately</td></tr>
            <tr><td>Fast one-off questions</td><td className="partial">overkill</td><td className="yes">Its home turf</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>ChatGPT wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">One-off questions</h3>
            <p>"Summarize this PDF." "Write a SQL query for X." "Translate this." ChatGPT is great. Draftlize for these is overkill — you don't need a substrate for things you'll never reference again.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Voice + multimodal + plugins</h3>
            <p>Voice chat, image understanding, the plugin ecosystem — these are real, useful things ChatGPT does. Draftlize is text-based and focused on a narrow problem.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">If chat IS the deliverable</h3>
            <p>For roles where the conversation itself is the work (customer support drafting, sales outreach, content generation), chat history is fine. Draftlize is for when the conversation is *upstream* of the deliverable.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Weeks of work that doesn't fall apart</h3>
            <p>The kind of work where you decide pricing in week 3 and need it still alive and citable in week 7's chat. Chat memory degrades; cards don't.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one thing, the rest follows</h3>
            <p>If changing decision A invalidates plan B, you need a tool that knows about the dependency. ChatGPT will happily generate plan B from outdated A and never tell you.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Many AI tools, one shared context</h3>
            <p>If you use Claude Code to implement, ChatGPT to brainstorm, Claude Desktop to read research — each tool remembers separately. Draftlize cards are the one context any of them can read and write via MCP.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ChatGPT is a great way to think out loud. Draftlize is where the thoughts go to live.
        </blockquote>
        <cite>You probably want both. They're not substitutes — they're layers.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep ChatGPT for the one-off questions. Add Draftlize for the multi-week product work where decisions need to compound.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
